"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import { getDefaultQueryRetry, WAVE_LOGS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiWaveLog } from "@/generated/models/ApiWaveLog";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
interface UseWaveActivityLogsProps {
  readonly waveId: string;
  readonly connectedProfileHandle: string | undefined;
  readonly reverse: boolean;
  readonly dropId: string | null;
  readonly logTypes: string[];
  readonly enabled?: boolean | undefined;
}
export function useWaveActivityLogs(t0) {
  const $ = _c(34);
  const {
    waveId,
    connectedProfileHandle,
    reverse,
    dropId,
    logTypes,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [logs, setLogs] = useState(t2);
  const canFetch = enabled && !!connectedProfileHandle;
  let t3;
  if ($[1] !== logTypes) {
    t3 = logTypes.join(",");
    $[1] = logTypes;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const serializedLogTypes = t3;
  let t4;
  if ($[3] !== dropId || $[4] !== serializedLogTypes || $[5] !== waveId) {
    t4 = [QueryKey.WAVE_LOGS, {
      waveId,
      limit: WAVE_LOGS_PARAMS.limit,
      dropId,
      logTypes: serializedLogTypes
    }];
    $[3] = dropId;
    $[4] = serializedLogTypes;
    $[5] = waveId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const queryKey = t4;
  let t5;
  let t6;
  if ($[7] !== canFetch || $[8] !== dropId || $[9] !== queryClient || $[10] !== queryKey || $[11] !== serializedLogTypes || $[12] !== waveId) {
    t5 = () => {
      if (!canFetch) {
        return;
      }
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t7 => {
          const {
            pageParam
          } = t7;
          const params = {
            limit: WAVE_LOGS_PARAMS.limit.toString()
          };
          if (dropId) {
            params.drop_id = dropId;
          }
          if (serializedLogTypes) {
            params.log_types = serializedLogTypes;
          }
          if (pageParam) {
            params.offset = `${pageParam}`;
          }
          return await commonApiFetch({
            endpoint: `waves/${waveId}/logs`,
            params
          });
        },
        initialPageParam: null,
        getNextPageParam: _temp,
        pages: 3,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
    };
    t6 = [canFetch, queryKey, waveId, dropId, serializedLogTypes, queryClient];
    $[7] = canFetch;
    $[8] = dropId;
    $[9] = queryClient;
    $[10] = queryKey;
    $[11] = serializedLogTypes;
    $[12] = waveId;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  let t7;
  if ($[15] !== dropId || $[16] !== serializedLogTypes || $[17] !== waveId) {
    t7 = async t8 => {
      const {
        pageParam: pageParam_0
      } = t8;
      const params_0 = {
        limit: WAVE_LOGS_PARAMS.limit.toString()
      };
      if (dropId) {
        params_0.drop_id = dropId;
      }
      if (serializedLogTypes) {
        params_0.log_types = serializedLogTypes;
      }
      if (pageParam_0 !== null) {
        params_0.offset = `${pageParam_0}`;
      }
      const results = await commonApiFetch({
        endpoint: `waves/${waveId}/logs`,
        params: params_0
      });
      return results;
    };
    $[15] = dropId;
    $[16] = serializedLogTypes;
    $[17] = waveId;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getDefaultQueryRetry();
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== canFetch || $[21] !== queryKey || $[22] !== t7) {
    t9 = {
      queryKey,
      queryFn: t7,
      initialPageParam: 0,
      getNextPageParam: _temp2,
      placeholderData: keepPreviousData,
      enabled: canFetch,
      staleTime: 60000,
      refetchInterval: 30000,
      ...t8
    };
    $[20] = canFetch;
    $[21] = queryKey;
    $[22] = t7;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage
  } = useInfiniteQuery(t9);
  let t10;
  let t11;
  if ($[24] !== data || $[25] !== reverse) {
    t10 = () => {
      setLogs(() => {
        const newLogs = data?.pages ? data.pages.flat() : [];
        return reverse ? newLogs.reverse() : newLogs;
      });
    };
    t11 = [data, reverse];
    $[24] = data;
    $[25] = reverse;
    $[26] = t10;
    $[27] = t11;
  } else {
    t10 = $[26];
    t11 = $[27];
  }
  useEffect(t10, t11);
  let t12;
  if ($[28] !== fetchNextPage || $[29] !== hasNextPage || $[30] !== isFetchingNextPage || $[31] !== isLoading || $[32] !== logs) {
    t12 = {
      logs,
      fetchNextPage,
      hasNextPage,
      isLoading,
      isFetchingNextPage
    };
    $[28] = fetchNextPage;
    $[29] = hasNextPage;
    $[30] = isFetchingNextPage;
    $[31] = isLoading;
    $[32] = logs;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp2(lastPage_0, allPages_0) {
  return lastPage_0.length === WAVE_LOGS_PARAMS.limit ? allPages_0.length * WAVE_LOGS_PARAMS.limit : null;
}
function _temp(lastPage, allPages) {
  return lastPage.length === WAVE_LOGS_PARAMS.limit ? allPages.length * WAVE_LOGS_PARAMS.limit : null;
}